
const normalizeKey = (s) => String(s).replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

export const pick = (obj, keys, fallback = "") => {
  if (!obj) return fallback;

  // 1) exact key match (fast path)
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "") {
      return obj[key];
    }
  }

  const objKeys = Object.keys(obj).map((k) => ({ key: k, norm: normalizeKey(k) }));

  for (const hint of keys) {
    const hintNorm = normalizeKey(hint);
    if (!hintNorm) continue;
    const match = objKeys.find((k) => k.norm === hintNorm);
    if (match && obj[match.key] !== undefined && obj[match.key] !== null && obj[match.key] !== "") {
      return obj[match.key];
    }
  }
  for (const hint of keys) {
    const hintNorm = normalizeKey(hint);
    if (!hintNorm) continue;
    const match = objKeys.find((k) => k.norm.endsWith(hintNorm));
    if (match && obj[match.key] !== undefined && obj[match.key] !== null && obj[match.key] !== "") {
      return obj[match.key];
    }
  }

  // 3) loosest fallback: real column contains the hint anywhere (handles
  // unknown prefixes AND unknown suffixes for entities we haven't confirmed
  // a sample response for yet, e.g. Department/Designation/Location/Grade).
  for (const hint of keys) {
    const hintNorm = normalizeKey(hint);
    if (!hintNorm || hintNorm.length < 3) continue;
    const match = objKeys.find((k) => k.norm.includes(hintNorm));
    if (match && obj[match.key] !== undefined && obj[match.key] !== null && obj[match.key] !== "") {
      return obj[match.key];
    }
  }

  return fallback;
};

export const getRowId = (row, extraKeys = []) =>
  pick(row, [...extraKeys, "Id", "id", "ID"], "");

export const getRowCode = (row, extraKeys = []) =>
  pick(row, [...extraKeys, "Code", "code"], "");

export const getRowName = (row, extraKeys = []) =>
  pick(row, [...extraKeys, "Name", "name"], "");

export const getRowIsActive = (row, extraKeys = []) => {
  const val = pick(row, [...extraKeys, "IsActive", "isActive"], true);
  if (typeof val === "string") return val === "1" || val.toLowerCase() === "true";
  if (typeof val === "number") return val === 1;
  return Boolean(val);
};

export const getRowRemarks = (row, extraKeys = []) =>
  pick(row, [...extraKeys, "Remarks", "remarks"], "");

// Current logged-in user's service no, used for p_*CreatedBy / p_*UpdatedBy
// fields. TEMPORARILY hardcoded to "3000807" per request while testing data
// insert/update - swap this back to reading the real logged-in user
// (localStorage.getItem("ServiceNo")) once that's ready to wire up again.
export const getCurrentServiceNo = () => "3000807";

export const getLoggedInServiceNo = () => {
  try {
    const user = JSON.parse(localStorage.getItem("ADU_EmployeeId") || "{}");
    return user.serviceNo ?? "";
  } catch {
    return "";
  }
}

export const getLoggedInUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem("ADU_EmployeeId") || "{}");
    return user.userId ?? "";
  } catch {
    return "";
  }
}

// Build a { code: label } lookup from a static {value,label} options list,
// e.g. GENDERS / MARITAL_STATUSES / RELIGIONS / NATIONALITIES / BLOOD_GROUPS.
// Used to decode a raw code coming back from the API into a readable label.
export const toCodeMap = (list) => {
  const map = {};
  (list || []).forEach((opt) => {
    if (opt && typeof opt === "object") {
      map[opt.value] = opt.label;
    } else {
      map[opt] = opt;
    }
  });
  return map;
};

// Build MUI-select-friendly {value, label} options from a master-data list,
// e.g. Departments / Designations / Grades / Locations / Employment Categories.
export const toSelectOptions = (list, idKeys = [], nameKeys = []) =>
  (list || []).map((row) => ({
    value: getRowId(row, idKeys),
    label: getRowName(row, nameKeys) || getRowCode(row, []),
  }));
